"use client";

import { useRef, useState, useEffect } from "react";
import Container from "@/components/ui/Container";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";

/* ── Story beats — color-coded for visual variety ─────────────────────────── */
const BEATS = [
  {
    id: "problem",
    eyebrow: "The Problem",
    heading: "Self-managed infra eats months.",
    body: "Self-managed nodes average 6–8 weeks of DevOps before your first production RPC call. Hardware spend spikes 3–5× under load. One config error drops your stack. No rollback. Five chains means five vendors, five dashboards, five on-call rotations.",
    color: "#F59E0B",
  },
  {
    id: "solution",
    eyebrow: "The Layer",
    heading: "One API. 50+ chains. Zero ops.",
    body: "Instanodes sits between your app and every chain you ship to. Smart routing picks the healthiest node per request. Auto-failover reroutes in under 500ms. Validators, rollups, and appchains, managed from one dashboard, billed on usage.",
    color: "#00D4FF",
  },
  {
    id: "result",
    eyebrow: "The Result",
    heading: "Minutes to first call. One on-call.",
    body: "First RPC call in minutes, not weeks. Mainnet validator in a dashboard click, not a DevOps hire. p99 < 90ms target across regions. 99.95% uptime SLA on the production tier. Infrastructure disappears. That's the point.",
    color: "#10B981",
  },
] as const;

/* ── Architecture diagram — driven by 0–1 progress ────────────────────────── */
function ArchDiagram({ progress, reduced }: { progress: number; reduced: boolean }) {
  const appOpacity   = Math.min(1, Math.max(0.15, (progress - 0.0) / 0.2 * 0.85 + 0.15));
  const layerOpacity = Math.min(1, Math.max(0.15, (progress - 0.3) / 0.15 * 0.85 + 0.15));
  const lineALOp     = Math.min(1, Math.max(0, (progress - 0.3) / 0.15));
  const nodeOpacity  = Math.min(1, Math.max(0.15, (progress - 0.5) / 0.2 * 0.85 + 0.15));
  const lineNOp      = Math.min(1, Math.max(0, (progress - 0.5) / 0.15));
  const badgeOpacity = Math.min(1, Math.max(0, (progress - 0.85) / 0.15));
  const badgeY       = 12 - badgeOpacity * 12;
  const ambientGlow  = 0.5 + progress * 0.5;

  // Beat-aware color: shifts based on progress
  const beatColor = progress < 0.36 ? "#F59E0B" : progress < 0.68 ? "#00D4FF" : "#10B981";

  return (
    <div className="relative h-full w-full">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{ opacity: ambientGlow }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse 70% 70% at 50% 50%, ${beatColor}2e 0%, transparent 70%)`,
            transition: "background 0.8s ease-out",
          }}
        />
      </div>

      <svg viewBox="0 0 360 320" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full" aria-hidden>
        <defs>
          <linearGradient id="hiw-layer-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={beatColor} stopOpacity="0.9" />
            <stop offset="100%" stopColor={beatColor} stopOpacity="0.6" />
          </linearGradient>
        </defs>

        {/* Static base lines */}
        <line x1="180" y1="68"  x2="180" y2="108" stroke="var(--line)" strokeWidth="1" strokeDasharray="3 3" />
        <line x1="120" y1="162" x2="60"  y2="210" stroke="var(--line)" strokeWidth="1" strokeDasharray="3 3" />
        <line x1="180" y1="162" x2="180" y2="210" stroke="var(--line)" strokeWidth="1" strokeDasharray="3 3" />
        <line x1="240" y1="162" x2="300" y2="210" stroke="var(--line)" strokeWidth="1" strokeDasharray="3 3" />

        {/* Your App */}
        <g style={{ opacity: appOpacity }}>
          <rect x="130" y="20" width="100" height="48" rx="10" fill={beatColor} fillOpacity="0.12" stroke={beatColor} strokeWidth="1" />
          <text x="180" y="40" textAnchor="middle" fill="var(--ink)" fontSize="10" fontWeight="700">YOUR APP</text>
          <text x="180" y="54" textAnchor="middle" fill="var(--ink-2)" fontSize="8">dApp · Protocol · Service</text>
        </g>

        {/* App → Layer line */}
        <g style={{ opacity: lineALOp }}>
          <line x1="180" y1="68" x2="180" y2="108" stroke={`${beatColor}b3`} strokeWidth="1.5" />
          <circle r="3.5" fill={beatColor}>
            {!reduced && <animateMotion dur="0.9s" repeatCount="indefinite" path="M180,68 L180,108" />}
          </circle>
        </g>

        {/* Instanodes Layer */}
        <g style={{ opacity: layerOpacity }}>
          <rect x="80" y="108" width="200" height="54" rx="10" fill="url(#hiw-layer-fill)" stroke={beatColor} strokeWidth="1.5" />
          <text x="180" y="129" textAnchor="middle" fill="var(--ink)" fontSize="10" fontWeight="700">INSTANODES</text>
          <text x="180" y="143" textAnchor="middle" fill="var(--ink-2)" fontSize="8">Smart Router · Load Balancer · Failover</text>
          <rect x="80" y="108" width="200" height="54" rx="10" fill="none" stroke={beatColor} strokeWidth="1.5">
            {!reduced && <animate attributeName="opacity" values="0.2;0.7;0.2" dur="2.4s" repeatCount="indefinite" />}
          </rect>
        </g>

        {/* Layer → Node lines */}
        <g style={{ opacity: lineNOp }}>
          {[
            { x1: 120, y1: 162, x2: 60,  y2: 210 },
            { x1: 180, y1: 162, x2: 180, y2: 210 },
            { x1: 240, y1: 162, x2: 300, y2: 210 },
          ].map((l, i) => (
            <g key={i}>
              <line {...l} stroke={`${beatColor}99`} strokeWidth="1.5" />
              <circle r="3" fill={beatColor}>
                {!reduced && (
                  <animateMotion dur={`${0.7 + i * 0.15}s`} repeatCount="indefinite" path={`M${l.x1},${l.y1} L${l.x2},${l.y2}`} />
                )}
              </circle>
            </g>
          ))}
        </g>

        {/* Node clusters */}
        <g style={{ opacity: nodeOpacity }}>
          {[
            { x: 20,  y: 210, label: "ETH Cluster", sub: "Archive · Full" },
            { x: 140, y: 210, label: "ARB Cluster", sub: "L2 · Rollup"    },
            { x: 260, y: 210, label: "SOL Cluster", sub: "High-TPS"       },
          ].map(({ x, y, label, sub }) => (
            <g key={label}>
              <rect x={x} y={y} width="80" height="48" rx="8" fill={beatColor} fillOpacity="0.10" stroke={beatColor} strokeWidth="1" />
              <text x={x + 40} y={y + 20} textAnchor="middle" fill="var(--ink)" fontSize="9" fontWeight="700">{label}</text>
              <text x={x + 40} y={y + 34} textAnchor="middle" fill="var(--ink-2)" fontSize="7.5">{sub}</text>
            </g>
          ))}
        </g>

        {/* Result badge */}
        <g style={{ opacity: badgeOpacity, transform: `translateY(${badgeY}px)` }}>
          <rect x="100" y="278" width="160" height="32" rx="16" fill={`${beatColor}24`} stroke={`${beatColor}73`} strokeWidth="1" />
          <text x="180" y="299" textAnchor="middle" fill={beatColor} fontSize="9" fontWeight="700">✓ READY · 4.8s deploy time</text>
        </g>
      </svg>
    </div>
  );
}

/* ── Section ─────────────────────────────────────────────────────────────────── */
export default function HowItWorks() {
  const sectionRef = useRef<HTMLElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const [activeBeat, setActiveBeat] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useGSAP(
    () => {
      if (!sceneRef.current || isMobile) return;

      const mm = gsap.matchMedia();

      mm.add(
        {
          isDesktop: "(min-width: 1024px)",
          noReduceMotion: "(prefers-reduced-motion: no-preference)",
        },
        (context) => {
          if (!context.conditions?.isDesktop || !context.conditions?.noReduceMotion) return;

          // Pin the scene and scrub through the architecture story.
          // No snap — it fights Lenis smooth scroll causing jumps.
          // pinType: "transform" works better with smooth scroll libs.
          ScrollTrigger.create({
            trigger: sceneRef.current,
            start: "top top",
            end: "+=2400",
            pin: true,
            pinType: "transform",
            scrub: 1,
            onUpdate: (self) => {
              const p = self.progress;
              setProgress(p);
              const next = p < 0.33 ? 0 : p < 0.66 ? 1 : 2;
              setActiveBeat((prev) => (prev !== next ? next : prev));
            },
          });

          // Text entrance per beat — stagger
          gsap.from(".hiw-eyebrow-rail", {
            autoAlpha: 0,
            y: 14,
            duration: 0.6,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 85%",
            },
          });

          // Gradient line draws in after eyebrow
          gsap.to(".hiw-eyebrow-line", {
            width: 120,
            duration: 0.8,
            delay: 0.3,
            ease: "power3.out",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 85%",
            },
          });
        },
      );
    },
    { scope: sectionRef, dependencies: [isMobile] },
  );

  // Mobile beats stagger entrance
  useGSAP(
    () => {
      if (!isMobile || !sectionRef.current) return;

      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        ScrollTrigger.batch(".hiw-mobile-beat", {
          start: "top 85%",
          onEnter: (batch) => {
            gsap.from(batch, {
              autoAlpha: 0,
              y: 40,
              scale: 0.97,
              stagger: 0.15,
              duration: 0.7,
              ease: "power3.out",
              overwrite: true,
            });
          },
        });
      });
    },
    { scope: sectionRef, dependencies: [isMobile] },
  );

  const beat = BEATS[activeBeat];

  return (
    <section ref={sectionRef} className="relative bg-surface" aria-label="How Instanodes works">
      {/* Section eyebrow + heading rail — theme-aware */}
      <div className="border-b border-line py-8">
        <Container>
          <div className="hiw-eyebrow-rail mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-ink-2">
              System architecture
            </p>
            <h2 className="font-accent text-[clamp(32px,4.5vw,52px)] font-normal leading-[1.06] tracking-[-0.025em] text-ink">
              How Instanodes <em className="italic text-accent">routes every call</em>.
            </h2>
            <p className="max-w-xl text-base leading-[1.55] text-ink-2">
              From your first request to a healthy node. Three beats, one platform.
            </p>
            <div
              className="hiw-eyebrow-line mt-2 h-px w-0 rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, transparent, var(--accent), transparent)",
              }}
            />
          </div>
        </Container>
      </div>

      {/* Mobile — non-sticky vertical stack, one diagram per beat */}
      {isMobile && (
        <div className="block lg:hidden">
          <Container>
            <div className="space-y-10 py-8">
              {BEATS.map((b, i) => {
                const mobileProgress = i === 0 ? 0.15 : i === 1 ? 0.5 : 0.95;
                return (
                  <div key={b.id} className="hiw-mobile-beat">
                    <div className="mx-auto h-[260px] w-full max-w-sm">
                      <ArchDiagram progress={mobileProgress} reduced={!!reduced} />
                    </div>
                    <div className="mt-6">
                      <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: b.color }}>{b.eyebrow}</p>
                      <h3 className="mb-4 text-2xl font-bold leading-[1.15] tracking-tight text-text-primary sm:text-3xl">{b.heading}</h3>
                      <p className="text-base leading-relaxed text-text-secondary">{b.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Container>
        </div>
      )}

      {/* Desktop — GSAP pinned scene */}
      {!isMobile && (
        <div
          ref={sceneRef}
          className="relative hidden lg:block"
        >
          <div className="flex h-screen items-start pt-16">
            <Container>
              <div className="grid grid-cols-2 items-center gap-16">
                {/* Left: pinned diagram */}
                <div className="relative h-[480px] w-full">
                  <ArchDiagram progress={reduced ? 1 : progress} reduced={!!reduced} />

                  {/* Beat dots — progress indicator */}
                  <div
                    className="absolute -bottom-12 left-1/2 flex -translate-x-1/2 gap-1.5"
                    role="tablist"
                    aria-label="Architecture story progress"
                  >
                    {BEATS.map((b, i) => (
                      <span
                        key={i}
                        role="tab"
                        aria-selected={activeBeat === i}
                        aria-label={b.eyebrow}
                        className="block h-1.5 rounded-full transition-all duration-300 ease-out"
                        style={{
                          width:      activeBeat === i ? 28 : 8,
                          background: activeBeat === i ? b.color : "rgba(255,255,255,0.18)",
                        }}
                      />
                    ))}
                  </div>

                  {/* Scroll progress bar under diagram */}
                  <div className="absolute -bottom-16 left-0 right-0 h-[2px] rounded-full bg-line">
                    <div
                      className="h-full rounded-full transition-all duration-200"
                      style={{
                        width: `${progress * 100}%`,
                        background: beat.color,
                      }}
                    />
                  </div>
                </div>

                {/* Right: active beat text */}
                <div className="relative flex h-[420px] flex-col justify-center">
                  {/* Accent line — shifts color per beat */}
                  <div
                    className="mb-6 h-px w-12 rounded-full transition-all duration-500"
                    style={{
                      background: beat.color,
                      boxShadow: `0 0 8px ${beat.color}40`,
                    }}
                  />
                  <div
                    key={beat.id}
                    className="transition-all duration-400"
                    style={{
                      animation: "fadeIn 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
                    }}
                  >
                    <p
                      className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em]"
                      style={{ color: beat.color }}
                    >
                      {beat.eyebrow}
                    </p>
                    <h3 className="mb-5 text-3xl font-bold leading-[1.15] tracking-tight text-text-primary sm:text-4xl">
                      {beat.heading}
                    </h3>
                    <p className="max-w-lg text-lg font-light leading-[1.6] text-text-secondary">
                      {beat.body}
                    </p>
                    {/* Beat step indicator */}
                    <p className="mt-6 font-mono text-[11px] tracking-widest" style={{ color: beat.color, opacity: 0.5 }}>
                      {`${activeBeat + 1} / ${BEATS.length}`}
                    </p>
                  </div>
                </div>
              </div>
            </Container>
          </div>
        </div>
      )}
    </section>
  );
}
